import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Button, Dialog, FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import CloseModalButton from '../../../../components/CloseModalButton';
import { StyledDialogFooter, StyledDialogTitle, StyledLargeDialogContent } from '../../../../components/Dialog.styled';
import { OpenSporetestModal, SporetestingResult } from '../types';
import { useAppDispatch } from '../../../../redux/store';
import { Biobox } from '../../../../types';
import { createSporeTest, createSporetestLog, deleteInOfficeSporeTest, updateSporeTest } from '../../../../services/biologicalServices';
import { addError, addSuccess } from '../../../../redux/alerts';
import useCustomerNumber from '../../../../hooks/useCustomerNumber';
import { getSporetestLog } from '../utils/helpers';
import { SporetestLogAction } from '../enums';

dayjs.extend(utc);

const initialFormState: SporetestingResult = {
  addressed_on: null,
  autoclave_id: 0,
  box_id: null,
  customer_id: '',
  note: null,
  received_date: null,
  result_description: '',
  sample_date: null,
  test_date: '',
  test_number: null,
  test_operator: null,
  test_result: null,
  test_result_id: 0,
  test_type: '',
};

interface AddEditSporetestModalProps {
  openModal: OpenSporetestModal,
  setOpenModal: Dispatch<SetStateAction<OpenSporetestModal>>
  bioboxes: Biobox[];
  sporetests: SporetestingResult[];
  reload: () => Promise<void>;
}

const AddEditSporetestModal: React.FC<AddEditSporetestModalProps> = ({
  openModal,
  setOpenModal,
  bioboxes,
  sporetests,
  reload,
}) => {
  const customerNumber = useCustomerNumber()
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState<SporetestingResult>(initialFormState);
  const [formErrors, setFormErrors] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);

  const { open, autoclaveId, testType, sporetest } = openModal

  useEffect(() => {
    const today = dayjs().startOf('day').toISOString()
    const data = sporetest ? structuredClone(sporetest) : structuredClone(formData)

    if (testType === 'inoffice' && !data.test_date) {
      data.test_date = today
    } else if (testType === 'mesa' && !data.sample_date) {
      data.sample_date = today
    }

    setFormData(data)
  }, [sporetest, testType])

  const mesaBoxes = useMemo(() => {
    return bioboxes
      .filter(box => box.active && box.box_type === 'mesa')
      .sort((a, b) => a.box_number.localeCompare(b.box_number));
  }, [bioboxes]);

  const mesaBoxSporeTests = useMemo(() => {
    if (!formData.box_id) return [];

    return sporetests
      .filter((test) => {
        return test.sample_date === null
          && test.received_date === null
          && test.test_date === null
          && Number(test.box_id) === Number(formData.box_id)
      })
      .sort((a, b) => (a.test_number ?? '').localeCompare(b.test_number ?? ''))
  }, [formData.box_id]);

  useEffect(() => {
    const selectedSporeTest = Boolean(formData.test_result_id)
      && sporetests.find(test => Number(test.test_result_id) === Number(formData.test_result_id))

    if (selectedSporeTest) {
      const cloned = structuredClone(selectedSporeTest)
      cloned.sample_date = formData.sample_date
      cloned.test_operator = formData.test_operator

      setFormData(cloned)
    }
  }, [formData.test_result_id]);

  const handleEditChange = (field: string, value: string | number | null) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      if (field === 'box_id') {
        updated.test_result_id = '';
      }

      return updated;
    });
  };

  const handleRemoveLog = async () => {
    setLoading(true);

    try {
      if (sporetest?.test_result_id && testType === 'inoffice') {
        await deleteInOfficeSporeTest(customerNumber, sporetest.test_result_id);

        dispatch(addSuccess('In-Office Spore Test was removed successfully'));
      } else if (sporetest?.test_result_id && testType === 'mesa') {
        const payload: SporetestingResult = {
          ...formData,
          autoclave_id: 0,
          sample_date: null,
          test_operator: null,
          test_result: null
        }

        await updateSporeTest(customerNumber, payload)

        const action = SporetestLogAction.Remove
        const sporetestLog = getSporetestLog(customerNumber, action, autoclaveId, sporetest)

        await createSporetestLog(customerNumber, sporetestLog)        

        dispatch(addSuccess('Mail-In Spore Test was updated successfully'));
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);

      dispatch(addError(error instanceof Error ? error.message : 'There was an error removing the sporetest, please try again later'));
      return;
    }

    reload();
    handleClose();
  }

  const handleSave = async () => {
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      setLoading(false);

      return;
    } else {
      setFormErrors({});
    }

    setLoading(true);

    const isUpdate = Boolean(sporetest?.test_result_id);

    try {
      if (testType === 'mesa') {
        const payload = structuredClone(formData)

        if (!isUpdate) {
          payload.autoclave_id = autoclaveId ?? 0
          payload.customer_id = customerNumber
          payload.result_description = ''
          payload.test_result = 'pending'
        }

        await updateSporeTest(customerNumber, payload)
        
        dispatch(
          addSuccess(`Mail-In Spore Test was ${isUpdate ? 'updated' : 'saved'} successfully`)
        )

        const action = isUpdate ? SporetestLogAction.Update : SporetestLogAction.Create
        const sporetestLog = getSporetestLog(customerNumber, action, autoclaveId, formData)

        await createSporetestLog(customerNumber, sporetestLog)
      } else {
        const payload = structuredClone(formData)

        payload.test_type = testType
        payload.customer_id = customerNumber
        payload.autoclave_id = autoclaveId ?? 0
        payload.addressed_on = null
        payload.result_description = formData.result_description || (formData.test_result ?? '')
        payload.note = null

        if (isUpdate) {
          await updateSporeTest(customerNumber, payload)
        } else {
          const {
            box_id,
            sample_date,
            test_number,
            test_result_id,
            ...createPayload
          } = payload

          await createSporeTest(customerNumber, createPayload)
        }

        dispatch(
          addSuccess(`In-Office Spore Test was ${isUpdate ? 'updated' : 'saved'} successfully`)
        )
      }

      reload();
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);

      dispatch(
        addError(error instanceof Error ? error.message : 'There was an error saving the sporetest, please try again later')
      );
    }

    handleClose();
  };

  const handleClose = () => {
    setOpenModal({ open: false, autoclaveId: null, testType: '', sporetest: null })
  }

  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    const fieldsToValidate = [
      { field: 'test_operator', label: 'Test Operator', value: formData?.test_operator },
      ...(testType === 'inoffice'
        ? [
          { field: 'test_result', label: 'Test Result', value: formData?.test_result },
          { field: 'test_date', label: 'Test Date', value: formData?.test_date },
        ]
        : []),
      ...(testType === 'mesa'
        ? [
          { field: 'box_id', label: 'Box Number', value: formData?.box_id },
          { field: 'sample_date', label: 'Test Date', value: formData?.sample_date },
          { field: 'test_result_id', label: 'Test Number', value: formData?.test_result_id }
        ]
        : [])
    ];

    fieldsToValidate.forEach(({ field, label, value }) => {
      if (!value) {
        errors[field] = `${label} is required`;
      }
    });

    return errors;
  };

  const displayRemoveLog = Boolean(((testType === 'mesa' && !sporetest?.test_date) || testType === 'inoffice') && sporetest?.test_result_id)

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <CloseModalButton handleClose={handleClose} />
      <StyledDialogTitle>
        {sporetest?.test_result_id ? 'Edit' : 'Log'} {testType === 'mesa' ? 'Mail-In' : 'In-Office'} Spore Test Result
      </StyledDialogTitle>
      <StyledLargeDialogContent>
        {testType === 'mesa' && !sporetest?.test_result_id && (
          <>
            <FormControl error={formErrors.box_id} fullWidth size="small" sx={{ mt: 2 }}>
              <InputLabel>Box Number</InputLabel>
              <Select label="Box Number" value={formData.box_id} onChange={(e) => handleEditChange('box_id', e.target.value ?? '')}>
                {mesaBoxes.map(box => (
                  <MenuItem key={box.box_id} value={box.box_id}>{box.box_number}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {formErrors.box_number && <FormHelperText error>{formErrors.box_number}</FormHelperText>}
            <FormControl disabled={!!(mesaBoxSporeTests.length === 0)} error={formErrors.test_result_id} fullWidth size="small" sx={{ mt: 2 }}>
              <InputLabel>Test Number</InputLabel>
              <Select label="Test Number" value={formData.test_result_id} onChange={(e) => handleEditChange('test_result_id', e.target.value)}>
                {mesaBoxSporeTests.map(test => (
                  <MenuItem key={test.test_result_id} value={test.test_result_id}>{test.test_number}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {formErrors.test_result_id && <FormHelperText error>{formErrors.test_result_id}</FormHelperText>}
            {formData.box_id && mesaBoxSporeTests.length === 0 && <FormHelperText error>There are no tests available for this box</FormHelperText>}
          </>
        )}
        {testType === 'mesa' && sporetest?.test_result_id && (
          <>
            <TextField
              label="Box Number"
              size="small"
              fullWidth
              sx={{ mt: 2 }}
              value={bioboxes.find(box => box.autoclave_id)?.box_number}
              disabled
            />
            <TextField
              label="Test Number"
              size="small"
              fullWidth
              sx={{ mt: 2 }}
              value={formData.test_number}
              disabled
            />
          </>
        )}
        <TextField
          label="Operator"
          placeholder="required"
          inputProps={{
            maxLength: 30
          }}
          size="small"
          fullWidth
          sx={{ mt: 2 }}
          error={!!formErrors.test_operator}
          value={formData.test_operator}
          onChange={(e) => handleEditChange('test_operator', e.target.value)}
        />
        {formErrors.test_operator && <FormHelperText error>{formErrors.test_operator}</FormHelperText>}
        {testType === 'mesa' && (
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
              <DatePicker
                label="Test Date"
                maxDate={dayjs().startOf('day')}
                slotProps={{ textField: { size: 'small', error: !!formErrors.sample_date } }}
                value={dayjs(formData.sample_date).utc()}
                onChange={sampleDate => {
                  handleEditChange('sample_date', sampleDate && sampleDate.toISOString())
                }}
                format="MM-DD-YYYY"
              />
            </LocalizationProvider>
          </FormControl>
        )}
        {testType === 'inoffice' && (
          <>
            <FormControl error={formErrors.test_result} fullWidth size="small" sx={{ mt: 2 }}>
              <InputLabel>Test Result</InputLabel>
              <Select label="Test Result" value={formData.test_result ?? ''} onChange={(e) => handleEditChange('test_result', e.target.value)}>
                <MenuItem value={'pass'}>Pass</MenuItem>
                <MenuItem value={'fail'}>Fail</MenuItem>
                <MenuItem value={'invalid'}>Invalid</MenuItem>
              </Select>
            </FormControl>
            {formErrors.test_result && <FormHelperText error>{formErrors.test_result}</FormHelperText>}
            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
                <DatePicker
                  label="Test Date"
                  maxDate={dayjs().startOf('day')}
                  slotProps={{ textField: { size: 'small', error: !!formErrors.test_date } }}
                  value={dayjs(formData?.test_date).utc()}
                  onChange={testDate => {
                    handleEditChange('test_date', testDate && testDate.toISOString())
                  }}
                  format="MM-DD-YYYY"
                />
              </LocalizationProvider>
            </FormControl>
            {formErrors.test_date && <FormHelperText error>{formErrors.test_date}</FormHelperText>}
            <TextField
              label="Description"
              placeholder="optional"
              inputProps={{
                maxLength: 140
              }}
              size="small"
              fullWidth
              sx={{ mt: 2 }}
              value={formData.result_description}
              onChange={(e) => handleEditChange('result_description', e.target.value)}
            />
          </>
        )}
      </StyledLargeDialogContent>
      <StyledDialogFooter>
        { displayRemoveLog && <Button onClick={handleRemoveLog} variant="contained" color="error" disabled={loading}>
          Remove Log
        </Button>}
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || (testType === 'mesa' && !sporetest?.test_result_id && mesaBoxSporeTests.length === 0)}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </StyledDialogFooter>
    </Dialog>
  );
};

export default AddEditSporetestModal;
